const lichcardsProductSearchExclusionPattern = /^(?:\s*NOT\s+product_type:Single\s+NOT\s+product_type:Singles(?=\s|$)\s*)+/i;

const lichcardsProductSearchQuery = (query) => {
  const cleanQuery = String(query || '')
    .replace(lichcardsProductSearchExclusionPattern, '')
    .trim();
  return cleanQuery;
};

class PredictiveSearch extends SearchForm {
  static instanceCount = 0;

  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.abortController = null;
    this.requestGeneration = 0;
    this.requestTimer = 0;
    this.liveRegionTimer = 0;
    this.renderedSearchTerm = null;
    this.dismissedQuery = null;
    this.searchTerm = '';
    this.resultsId = `predictive-search-${++PredictiveSearch.instanceCount}-results`;
    this.statusElement = this.querySelector('.predictive-search-status');
    this.errorText = this.getAttribute('data-error-text');
    this.unavailableMessage = null;

    this.prepareResultsAccessibility();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.cancelRequest();
    window.clearTimeout(this.liveRegionTimer);
  }

  cancelRequest() {
    this.requestGeneration += 1;
    this.abortController?.abort();
    this.abortController = null;
    window.clearTimeout(this.requestTimer);
    this.requestTimer = 0;
  }

  beginRequest() {
    this.hideUnavailableMessage();
    this.cancelRequest();
    this.abortController = new AbortController();
    const request = {
      generation: this.requestGeneration,
      signal: this.abortController.signal,
      query: this.getQuery(),
    };
    this.requestTimer = window.setTimeout(() => {
      if (this.isCurrentRequest(request)) this.showUnavailableMessage();
    }, 8000);
    return request;
  }

  isCurrentRequest(request) {
    return this.isConnected && request.generation === this.requestGeneration &&
      !request.signal.aborted && request.query === this.getQuery();
  }

  finishRequest(request) {
    if (request.generation !== this.requestGeneration) return;
    window.clearTimeout(this.requestTimer);
    this.requestTimer = 0;
    this.abortController = null;
  }

  prepareResultsAccessibility() {
    this.predictiveSearchResults.id = this.resultsId;
    this.predictiveSearchResults.setAttribute('role', 'listbox');
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.setAttribute('aria-owns', this.resultsId);
    this.input.removeAttribute('aria-activedescendant');

    const content = this.predictiveSearchResults.querySelector('#predictive-search-results');
    if (content) {
      content.id = `${this.resultsId}-content`;
      content.removeAttribute('aria-expanded');
    }
    this.predictiveSearchResults.querySelectorAll('[role="listbox"]').forEach((list) => {
      list.setAttribute('role', 'group');
    });
    this.predictiveSearchResults.querySelectorAll('li, button.predictive-search__item').forEach((option, index) => {
      if (!option.matches('button') && !option.querySelector('a[href], button:not([disabled])')) return;
      option.id = `${this.resultsId}-option-${index + 1}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
    });
  }

  setupEventListeners() {
    if (this.input?.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.predictiveSearchResults.addEventListener('click', (event) => {
      if (!this.hasCurrentResults() || !this.isOpen) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    this.input.addEventListener('click', () => {
      if (this.closest('.header__search')?.querySelector('.search-modal__form') &&
        this.getAttribute('results') === 'true' && this.renderedSearchTerm === this.getQuery()) {
        this.dismissedQuery = null;
        this.open();
      }
    });
  }

  hideUnavailableMessage() {
    if (this.unavailableMessage) this.unavailableMessage.hidden = true;
  }

  showUnavailableMessage() {
    this.close();
    this.removeAttribute('results');
    this.renderedSearchTerm = null;
    this.predictiveSearchResults.replaceChildren();
    if (!this.errorText) return;

    if (!this.unavailableMessage) {
      const message = document.createElement('p');
      message.className = 'w-full m-0';
      message.setAttribute('data-predictive-search-unavailable', '');
      message.textContent = this.errorText;
      const choices = this.querySelector('.lc-search-scope-actions');
      if (choices) choices.prepend(message);
      else this.input.form?.appendChild(message);
      this.unavailableMessage = message;
    }
    this.unavailableMessage.hidden = false;
    this.setLiveRegionText(this.errorText);
  }

  getQuery() {
    return this.input.value.trim();
  }

  hasCurrentResults() {
    const query = this.getQuery();
    return this.getAttribute('results') === 'true' && this.renderedSearchTerm === query &&
      this.dismissedQuery !== query;
  }

  onInput() {
    this.hideUnavailableMessage();
    this.dismissedQuery = null;
    this.toggleResetButton();
    // Debounced requests must never leave an older query selectable in the meantime.
    if (this.renderedSearchTerm !== this.getQuery()) this.close();
  }

  onChange() {
    if (!this.isConnected) return;
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (this.dismissedQuery === newSearchTerm) return;
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      this.renderEmptyState();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || (this.hasCurrentResults() && this.isOpen &&
      this.predictiveSearchResults.querySelector('[aria-selected="true"] a'))) event.preventDefault();
  }

  onFormReset(event) {
    event.preventDefault();
    this.dismissedQuery = null;
    this.close(true);
    this.toggleResetButton();
    if (document.activeElement === this.input) this.renderEmptyState();
    else this.input.focus();
  }

  async renderEmptyState() {
    const request = this.beginRequest();
    try {
      const emptySectionId = 'predictive-search-empty';
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.set('section_id', emptySectionId);
      url.searchParams.sort();

      const response = await fetch(`${url.toString()}`, {
        signal: request.signal,
      });

      if (!this.isCurrentRequest(request)) return;

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      if (!this.isCurrentRequest(request)) return;

      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search-empty');

      if (parsedEmptySectionMarkup) {
        const emptyStateContent = parsedEmptySectionMarkup.querySelector('#predictive-search-results');

        if (emptyStateContent) {
          if (!emptyStateContent.textContent.trim() && !emptyStateContent.querySelector('a[href], button, [role="option"]')) {
            this.renderedSearchTerm = null;
            this.removeAttribute('results');
            this.close();
            return;
          }
          this.predictiveSearchResults.innerHTML = emptyStateContent.innerHTML;
          this.prepareResultsAccessibility();
          this.renderedSearchTerm = request.query;
          this.setAttribute('results', true);
          this.removeAttribute('loading');
          if (this.contains(document.activeElement)) this.open();
        }
      }
    } catch (error) {
      if (!this.isCurrentRequest(request)) return;
      if (error?.name === 'AbortError' || error?.code === 20) { this.close(); return; }
      this.showUnavailableMessage();
      console.error('Error rendering empty state:', error);
    } finally {
      this.finishRequest(request);
    }
  }

  onFocus() {
    this.dismissedQuery = null;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      if (this.getAttribute('results') === 'true' && this.renderedSearchTerm === '') this.open();
      else this.renderEmptyState();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true' && this.renderedSearchTerm === currentSearchTerm) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.target !== this.input) return;

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
      case 'NumpadEnter':
        event.preventDefault();
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'Escape') {
      event.preventDefault();
      this.dismissedQuery = this.getQuery();
      this.hideUnavailableMessage();
      this.close();
      return;
    }

    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.target === this.input && (event.code === 'ArrowUp' || event.code === 'ArrowDown')) {
      event.preventDefault();
    }

    // Init predictive search tabs component
    const tabsComponent = this.querySelector('tabs-component');
    if(tabsComponent) {
      tabsComponent.init();
      // this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    };

  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
      //   // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
      //   return;
      // }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open') && this.getAttribute('results') === 'true' &&
      this.renderedSearchTerm === this.getQuery()) {
      this.dismissedQuery = null;
      this.open();
    }
    if (!this.getAttribute('open') || !this.hasCurrentResults()) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.predictiveSearchResults.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);

    // Virtual keyboard focus needs to reveal the option without moving the page.
    const scrollBoundary = this.closest('side-drawer') || this;
    for (let container = activeElement.parentElement; container && scrollBoundary.contains(container); container = container.parentElement) {
      const overflowY = getComputedStyle(container).overflowY;
      if (!/^(auto|scroll)$/.test(overflowY) || container.scrollHeight <= container.clientHeight + 1) continue;

      const optionRect = activeElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const visibleTop = containerRect.top + container.clientTop + 4;
      const visibleBottom = containerRect.top + container.clientTop + container.clientHeight - 4;
      if (optionRect.top < visibleTop) {
        container.scrollTop += Math.floor(optionRect.top - visibleTop);
      } else if (optionRect.bottom > visibleBottom) {
        container.scrollTop += Math.ceil(optionRect.bottom - visibleBottom);
      }
      break;
    }
  }

  selectOption() {
    if (!this.isOpen || !this.hasCurrentResults()) return;
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    if (this.getAttribute('unsupported-locale') === 'true') {
      this.renderEmptyState();
      return;
    }
    const request = this.beginRequest();
    const queryKey = searchTerm.trim().toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], request.query);
      this.finishRequest(request);
      return;
    }

    const requestUrl = new URL(routes.predictive_search_url, window.location.origin);
    requestUrl.searchParams.set('q', lichcardsProductSearchQuery(searchTerm));
    requestUrl.searchParams.set('resources[type]', 'product');
    requestUrl.searchParams.set('resources[limit]', '10');
    requestUrl.searchParams.set('resources[limit_scope]', 'each');
    requestUrl.searchParams.set('resources[options][unavailable_products]', 'hide');
    requestUrl.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,variants.sku',
    );
    requestUrl.searchParams.set('section_id', 'predictive-search');

    fetch(requestUrl.toString(), {
      signal: request.signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!this.isCurrentRequest(request)) return null;
        if (response.status === 417) {
          this.setAttribute('unsupported-locale', 'true');
          this.close();
          this.renderEmptyState();
          return null;
        }
        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text();
      })
      .then((text) => {
        if (!text || !this.isCurrentRequest(request)) return;
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Missing predictive search section');
        const resultsMarkup = section.innerHTML;
        document.querySelectorAll('predictive-search').forEach((predictiveSearchInstance) => {
          if (!predictiveSearchInstance.cachedResults) return;
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup, request.query);
      })
      .catch((error) => {
        if (!this.isCurrentRequest(request)) return;
        if (error?.name === 'AbortError' || error?.code === 20) { this.close(); return; }
        this.showUnavailableMessage();
        console.error('Predictive search error:', error);
      })
      .finally(() => this.finishRequest(request));
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    window.clearTimeout(this.liveRegionTimer);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';

    this.liveRegionTimer = window.setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm = this.getQuery()) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.prepareResultsAccessibility();
    this.renderedSearchTerm = searchTerm;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    if (this.contains(document.activeElement)) this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.header-group-section').getBoundingClientRect().bottom;

    return this.resultsMaxHeight;
  }

  open() {
    if (!this.hasCurrentResults()) return;
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.cancelRequest();
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.renderedSearchTerm = null;
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.removeAttribute('aria-activedescendant');
    if (this.hasAttribute('loading')) this.setLiveRegionText('');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
